import { AuthenticationManager } from '@frontend/authentication-v2';
import { useLocalStorageState } from '@frontend/common';
import { FullscreenSpinner } from '@frontend/components/common';
import { FunctionalityClient } from '@frontend/functionality/api';
import { Application, FeatureTarget, Functionality, FunctionalityType } from '@frontend/functionality/types';
import { useAccountRepository } from '@frontend/repository';
import { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const MenuStoreContext = createContext<{
    isLoading: boolean;
    functionalities: Functionality[];
    applications: (Application | 'my')[];
    currentApp: Application | 'my' | null;
    previouslyUsedApps: { appType: Application | 'my'; lastUse: number }[];
    isValidLicense: (application: Application) => boolean;
    setCurrentApp: React.Dispatch<React.SetStateAction<Application | 'my' | null>>;
    getFunctionalitiesByApplication: (application: Application, type?: FunctionalityType) => Functionality[];
} | null>(null);

export const MenuStoreProvider = (props: { children: React.ReactNode }) => {
    const { currentAccount } = useAccountRepository();
    const [isLoading, setIsLoading] = useState(true);
    const [previouslyUsedApps, setPrevieuslyUsedApps] = useLocalStorageState<{ appType: Application | 'my'; lastUse: number }[]>('previeusly_used_apps', []); //apps should always be sorted on lastUse before storing
    const [selectedAppType, setSelectedAppType] = useLocalStorageState<string | null>(
        'selected_app',
        localStorage.getItem('selected_app') ? JSON.parse(localStorage.getItem('selected_app')!) : null
    );
    const [functionalities, setFunctionalities] = useState<Functionality[] | null>(null);
    const corporateFunctionalities = functionalities ? functionalities.filter((func) => func.type === FunctionalityType.CORPORATE) : [];
    const personalFunctionalities = functionalities ? functionalities.filter((func) => func.type === FunctionalityType.PERSONAL) : [];
    const [currentApp, setCurrentApp] = useState<Application | 'my' | null>(
        selectedAppType ? (selectedAppType as Application) : previouslyUsedApps.length > 0 ? previouslyUsedApps[0].appType : null
    );
    const [locationVersion, setLocationVersion] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        const handleLocationChange = () => setLocationVersion((v) => v + 1);
        window.addEventListener('local-storage:mobile-current-location', handleLocationChange);
        return () => window.removeEventListener('local-storage:mobile-current-location', handleLocationChange);
    }, []);

    useEffect(() => {
        AuthenticationManager.getInstance()
            .getTokenWhenReady()
            .then(async (token) => {
                if (!token) return;

                await FunctionalityClient.fetchFunctionalities({
                    account_id: currentAccount ?? token.account_id,
                    target: [FeatureTarget.MOBILE, FeatureTarget.API]
                })
                    .then((res) => {
                        setFunctionalities(res.results);
                    })
                    .catch((error) => {
                        setFunctionalities([]);
                    });

                return;
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [currentAccount, locationVersion]);

    function isValidLicense(application: Application): boolean {
        return !!functionalities?.find((func) => func.application === application);
    }

    function getFunctionalitiesByApplication(application: Application, type?: FunctionalityType): Functionality[] {
        return functionalities?.filter((f) => f.application === application && (!type || f.type === type)) || [];
    }

    useEffect(() => {
        if (currentApp) {
            setSelectedAppType(currentApp);
            const previouslyUsedAppsCopy = [...previouslyUsedApps];
            const foundApp = previouslyUsedAppsCopy.find((app) => app.appType === currentApp);
            if (!foundApp) {
                previouslyUsedAppsCopy.push({ appType: currentApp, lastUse: Date.now() });
            } else foundApp.lastUse = Date.now();
            setPrevieuslyUsedApps(previouslyUsedAppsCopy.sort((a, b) => b.lastUse - a.lastUse));
            navigate(`${currentApp}/home`);
        } else {
            navigate(`/home`);
        }
    }, [currentApp]);

    useEffect(() => {
        if (functionalities) {
            if (currentApp) {
                if (currentApp === 'my') {
                    if (personalFunctionalities.length === 0) setCurrentApp(null);
                } else if (corporateFunctionalities.find((l) => l.application === currentApp) === undefined) {
                    if (previouslyUsedApps) {
                        const foundApp = previouslyUsedApps.find((app) => corporateFunctionalities.find((l) => l.application === app.appType));
                        if (foundApp) {
                            setCurrentApp(foundApp.appType);
                            return;
                        }
                    }
                    if (corporateFunctionalities.length > 0) {
                        setCurrentApp(corporateFunctionalities[0].application);
                    } else setCurrentApp(null);
                }
            } else {
                if (personalFunctionalities.length > 0) {
                    setCurrentApp('my');
                } else if (corporateFunctionalities.length > 0) {
                    setCurrentApp(corporateFunctionalities[0].application);
                }
            }
        }
    }, [functionalities, currentApp]);

    if (functionalities === null)
        return (
            <FullscreenSpinner
                screenClassName='bg-background-surface'
                spinner={{ className: 'text-brand-light fill-brand-primary size-16' }}
            />
        );

    const reducedCorporateApplications = corporateFunctionalities.reduce((apps, func) => {
        if (!apps.includes(func.application)) {
            apps.push(func.application);
        }
        return apps;
    }, [] as Application[]);

    if (functionalities === null) return <LoadingScreen />;
    return (
        <MenuStoreContext.Provider
            value={{
                isLoading,
                currentApp,
                applications: [...((personalFunctionalities.length > 0 ? ['my'] : []) as (Application | 'my')[]), ...reducedCorporateApplications],
                functionalities,
                previouslyUsedApps,
                isValidLicense,
                setCurrentApp,
                getFunctionalitiesByApplication
            }}>
            {props.children}
        </MenuStoreContext.Provider>
    );
};

export const useMenuRepository = () => {
    const context = useContext(MenuStoreContext);
    if (!context) {
        throw new Error('useMenuRepository must be used within an MenuStoreProvider');
    }
    return context;
};

const LoadingScreen = () => {
    return (
        <div className='flex min-h-screen min-w-screen items-center justify-center bg-white'>
            <div className='flex flex-col items-center gap-8'>
                <div role='status'>
                    <svg
                        aria-hidden='true'
                        className={`fill-brand-primarytext-brand-light h-8 w-8 animate-spin`}
                        viewBox='0 0 100 101'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'>
                        <path
                            d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
                            fill='currentColor'
                        />
                        <path
                            d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
                            fill='currentFill'
                        />
                    </svg>
                    <span className='sr-only'>Loading...</span>
                </div>
            </div>
        </div>
    );
};
